import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  FileEdit, Eye, Save, RotateCcw, X, Code, CheckCircle2, AlertCircle, AlertTriangle, Plus, Search,
  Bold, Italic, Underline, AlignLeft, AlignCenter, AlignRight, AlignJustify, List, ListOrdered,
  Eraser, Undo2, Redo2, FileDown, Check, Trash2,
} from 'lucide-react';
import api from '../services/api';
import {
  DOC_LANGUAGES, FIELD_GROUPS, DEFAULT_PAGE_SETTINGS, PAGE_FORMATS,
  normalizeLanguage, fieldLabel, isSystemField, labelToKey, chipHtml, toEditorHtml, usedFieldKeys,
  decorateChips, toStoredHtml, fillHtml, documentPageCss, measureFit, mmToPx, pageFormatLabel,
} from '../utils/documentTemplates';
import './InAppTemplateEditorModal.css';

/**
 * Word-style document editor shared by template management and certificate generation.
 *
 * mode="template" (default): edit and save the template of a document type, in
 *   French and English. Dynamic fields are inserted from a list and appear as
 *   named chips; nothing has to be typed between braces.
 * mode="generate": the same page, filled with the employee's data for the request
 *   `requestId`. Field values are edited in the side panel and update the page
 *   live; the footer action calls `onGenerate({ html, variables, notes, language })`.
 *
 * The page on screen has the real size and margins of the template (taken from
 * the uploaded Word file), which is also what the PDF and the printout use.
 */

const PAYSLIP_TYPE = 'payslip';

const PAYSLIP_SAMPLE_VALUES = {
  companyName: 'AGREGATECH SOLUTIONS', cnssEmployerNumber: '123456-78', month: 'Octobre', year: String(new Date().getFullYear()),
  fullName: 'Prénom Nom', matricule: 'MAT-00001', cin: '12345678', position: 'Ingénieur', department: 'IT', cnss: '12345678-90',
  regularHours: '173', overtimeHours: '8', salary: '2 500,000', grossSalary: '2 650,000', cnssDeduction: '243,270',
  irppTax: '310,000', netSalary: '2 096,730', paymentMethod: 'Virement', defaultBank: 'BIAT',
  date: new Date().toLocaleDateString('fr-FR'), hourlyRate: '14,450',
};

const getPayslipSampleHtml = () => `
<table style="width: 100%; border-bottom: 2px solid #2563eb; margin-bottom: 18px;">
  <tr>
    <td style="padding-bottom: 10px;">
      <p style="margin: 0; color: #2563eb; font-size: 16pt; font-weight: bold;">{companyName}</p>
      <p style="margin: 3px 0 0 0; font-size: 9pt; color: #64748b;">N° CNSS Employeur : {cnssEmployerNumber}</p>
    </td>
    <td style="padding-bottom: 10px; text-align: right;">
      <p style="margin: 0; color: #2563eb; font-size: 15pt; font-weight: bold;">BULLETIN DE PAIE</p>
      <p style="margin: 3px 0 0 0; font-size: 10pt; font-weight: bold;">Période : {month} {year}</p>
    </td>
  </tr>
</table>
<table style="width: 100%; background: #f8fafc; border: 1px solid #e2e8f0; margin-bottom: 18px;">
  <tr>
    <td style="padding: 10px; width: 50%;">
      <p style="margin: 2px 0;"><strong>Nom &amp; Prénom :</strong> {fullName}</p>
      <p style="margin: 2px 0;"><strong>Matricule :</strong> {matricule}</p>
      <p style="margin: 2px 0;"><strong>N° CIN :</strong> {cin}</p>
    </td>
    <td style="padding: 10px;">
      <p style="margin: 2px 0;"><strong>Poste / Fonction :</strong> {position}</p>
      <p style="margin: 2px 0;"><strong>Département :</strong> {department}</p>
      <p style="margin: 2px 0;"><strong>N° CNSS Salarié :</strong> {cnss}</p>
    </td>
  </tr>
</table>
<table style="width: 100%; margin-bottom: 18px; font-size: 10pt;">
  <tr style="background: #2563eb; color: #ffffff;">
    <th style="padding: 8px; border: 1px solid #2563eb; text-align: left;">Rubrique</th>
    <th style="padding: 8px; border: 1px solid #2563eb;">Nombre / Heures</th>
    <th style="padding: 8px; border: 1px solid #2563eb; text-align: right;">Gains</th>
    <th style="padding: 8px; border: 1px solid #2563eb; text-align: right;">Retenues</th>
  </tr>
  <tr>
    <td style="padding: 8px; border: 1px solid #e2e8f0;">Heures normales</td>
    <td style="padding: 8px; border: 1px solid #e2e8f0; text-align: center;">{regularHours} h</td>
    <td style="padding: 8px; border: 1px solid #e2e8f0; text-align: right;">{salary}</td>
    <td style="padding: 8px; border: 1px solid #e2e8f0; text-align: right;">-</td>
  </tr>
  <tr>
    <td style="padding: 8px; border: 1px solid #e2e8f0;">Heures supplémentaires</td>
    <td style="padding: 8px; border: 1px solid #e2e8f0; text-align: center;">{overtimeHours} h</td>
    <td style="padding: 8px; border: 1px solid #e2e8f0; text-align: right;">-</td>
    <td style="padding: 8px; border: 1px solid #e2e8f0; text-align: right;">-</td>
  </tr>
  <tr>
    <td style="padding: 8px; border: 1px solid #e2e8f0;">Cotisation CNSS salariale</td>
    <td style="padding: 8px; border: 1px solid #e2e8f0; text-align: center;">9,18 %</td>
    <td style="padding: 8px; border: 1px solid #e2e8f0; text-align: right;">-</td>
    <td style="padding: 8px; border: 1px solid #e2e8f0; text-align: right;">{cnssDeduction}</td>
  </tr>
  <tr style="font-weight: bold; background: #f1f5f9;">
    <td style="padding: 8px; border: 1px solid #cbd5e1;" colspan="2">TOTAL BRUT &amp; RETENUES</td>
    <td style="padding: 8px; border: 1px solid #cbd5e1; text-align: right;">{grossSalary}</td>
    <td style="padding: 8px; border: 1px solid #cbd5e1; text-align: right;">{cnssDeduction}</td>
  </tr>
</table>
<table style="width: 100%; background: #2563eb; color: #ffffff; margin-bottom: 18px;">
  <tr>
    <td style="padding: 12px; font-size: 12pt; font-weight: bold;">NET À PAYER :</td>
    <td style="padding: 12px; font-size: 16pt; font-weight: bold; text-align: right;">{netSalary}</td>
  </tr>
</table>
<table style="width: 100%; font-size: 9pt; color: #64748b; border-top: 1px solid #e2e8f0;">
  <tr>
    <td style="padding-top: 8px;">Mode de paiement : <strong>{paymentMethod} ({defaultBank})</strong></td>
    <td style="padding-top: 8px; text-align: right;">Fait le : <strong>{date}</strong></td>
  </tr>
</table>`.trim();

/** Chips back to the `{key}` notation (the payslip template keeps the legacy storage format). */
const toBraceHtml = (html) => {
  const root = document.createElement('div');
  root.innerHTML = html;
  root.querySelectorAll('.doc-var[data-var]').forEach((chip) => {
    chip.replaceWith(document.createTextNode(`{${chip.dataset.var}}`));
  });
  return root.innerHTML;
};

const FONT_SIZES = [8, 9, 10, 11, 12, 14, 16, 18, 20, 24, 28];

// Stable reference so effects depending on the values do not re-run on every render
const NO_VALUES = {};

const keepSelection = (e) => e.preventDefault();

const FieldCatalog = ({ groups, customFields, lang, search, usedKeys, onInsert, onDeleteCustom, disabled }) => {
  const query = search.trim().toLowerCase();
  const matches = (label) => !query || label.toLowerCase().includes(query);
  const visibleGroups = groups
    .map((group) => ({ ...group, fields: group.fields.filter((field) => matches(field[lang] || field.fr)) }))
    .filter((group) => group.fields.length > 0);
  const visibleCustom = customFields.filter((field) => matches(field.label));

  return (
    <div className="dte-catalog">
      {visibleGroups.map((group) => (
        <div className="dte-catalog-group" key={group.id}>
          <div className="dte-catalog-title">{group[lang] || group.fr}</div>
          <div className="dte-catalog-fields">
            {group.fields.map((field) => (
              <button
                key={field.key}
                type="button"
                className={`dte-field-btn${usedKeys.includes(field.key) ? ' is-used' : ''}`}
                onMouseDown={keepSelection}
                onClick={() => onInsert(field.key)}
                disabled={disabled}
                title="Cliquer pour insérer dans le document"
              >
                {usedKeys.includes(field.key) ? <Check size={11} /> : <Plus size={11} />}
                {field[lang] || field.fr}
              </button>
            ))}
          </div>
        </div>
      ))}
      {visibleCustom.length > 0 && (
        <div className="dte-catalog-group">
          <div className="dte-catalog-title">Champs personnalisés</div>
          <div className="dte-catalog-fields">
            {visibleCustom.map((field) => (
              <span className="dte-custom-field" key={field.key}>
                <button
                  type="button"
                  className={`dte-field-btn is-custom${usedKeys.includes(field.key) ? ' is-used' : ''}`}
                  onMouseDown={keepSelection}
                  onClick={() => onInsert(field.key)}
                  disabled={disabled}
                >
                  {usedKeys.includes(field.key) ? <Check size={11} /> : <Plus size={11} />}
                  {field.label}
                </button>
                {onDeleteCustom && (
                  <button
                    type="button"
                    className="dte-field-delete"
                    onClick={() => onDeleteCustom(field.key)}
                    title="Supprimer ce champ"
                  >
                    <Trash2 size={11} />
                  </button>
                )}
              </span>
            ))}
          </div>
        </div>
      )}
      {visibleGroups.length === 0 && visibleCustom.length === 0 && (
        <p className="dte-empty">Aucun champ ne correspond à « {search} ».</p>
      )}
    </div>
  );
};

const InAppTemplateEditorModal = ({
  isOpen,
  onClose,
  templateType,
  templateName,
  initialHtml,
  initialLanguage,
  onSaveSuccess,
  mode = 'template',
  requestId,
  employeeLabel,
  onGenerate,
  generating = false,
}) => {
  const isGenerate = mode === 'generate';
  const isPayslip = templateType === PAYSLIP_TYPE;

  const [lang, setLang] = useState('fr');
  const [tab, setTab] = useState('edit'); // 'edit' | 'preview' | 'html'
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [pages, setPages] = useState({}); // language -> page settings
  const [valuesByLang, setValuesByLang] = useState({}); // language -> field values (employee or sample)
  const [docInfo, setDocInfo] = useState({}); // language -> { isDefault }
  const [languagesInfo, setLanguagesInfo] = useState(null);
  const [customFields, setCustomFields] = useState([]);
  const [dirty, setDirty] = useState({});
  const [usedKeys, setUsedKeys] = useState([]);
  const [fit, setFit] = useState({ ratio: 0, overflow: false });
  const [previewZoom, setPreviewZoom] = useState(1);
  const [search, setSearch] = useState('');
  const [notes, setNotes] = useState('');
  const [newField, setNewField] = useState(null); // { label, defaultVal } while the form is open
  const [loadToken, setLoadToken] = useState(0);

  // Stored-form HTML of each language; the canvas DOM is the live copy while editing
  const docsRef = useRef({});
  const overridesRef = useRef({}); // values typed by the admin survive a language switch
  const canvasRef = useRef(null);
  const previewRef = useRef(null);
  const lastRangeRef = useRef(null);

  const page = pages[lang] || DEFAULT_PAGE_SETTINGS;
  const values = valuesByLang[lang] || NO_VALUES;
  const labelOf = useCallback((key) => fieldLabel(key, lang, customFields), [lang, customFields]);

  const showMessage = (type, text) => setMessage({ type, text });

  const loadLanguage = useCallback(async (targetLang, { forceDefault = false } = {}) => {
    setLoading(true);
    try {
      let html = '';
      let pageSettings = DEFAULT_PAGE_SETTINGS;
      let fields = [];
      let fieldValues = {};
      let isDefault = false;

      if (isPayslip) {
        const useSample = forceDefault || !initialHtml || !initialHtml.trim();
        html = useSample ? getPayslipSampleHtml() : initialHtml;
        isDefault = useSample;
        fieldValues = PAYSLIP_SAMPLE_VALUES;
      } else if (isGenerate) {
        const res = await api.get(`/attestations/${requestId}/preview?lang=${targetLang}`);
        html = res.templateHtml || '';
        pageSettings = res.pageSettings || DEFAULT_PAGE_SETTINGS;
        fields = res.customFields || [];
        fieldValues = { ...(res.variables || {}), ...overridesRef.current };
        isDefault = !!res.isDefaultTemplate;
        setLanguagesInfo(res.languages || null);
      } else {
        const res = await api.get(
          `/attestations/templates/${templateType}/editor?lang=${targetLang}${forceDefault ? '&default=1' : ''}`,
        );
        html = res.html || '';
        pageSettings = res.pageSettings || DEFAULT_PAGE_SETTINGS;
        fields = res.customFields || [];
        fieldValues = res.sampleValues || {};
        isDefault = !!res.isDefault;
        setLanguagesInfo(res.languages || null);
      }

      // Fields found in the document that are neither built-in nor declared become custom fields
      const prepared = toEditorHtml(html, (key) => fieldLabel(key, targetLang, fields));
      const known = [...fields];
      prepared.keys.forEach((key) => {
        if (!isSystemField(key) && !known.some((field) => field.key === key)) {
          known.push({ key, label: fieldLabel(key, targetLang, []), defaultVal: '' });
        }
      });

      docsRef.current[targetLang] = prepared.html;
      setCustomFields((prev) => {
        const merged = [...prev];
        known.forEach((field) => {
          if (!merged.some((existing) => existing.key === field.key)) merged.push(field);
        });
        return merged;
      });
      setPages((prev) => ({ ...prev, [targetLang]: pageSettings }));
      setValuesByLang((prev) => ({ ...prev, [targetLang]: fieldValues }));
      setDocInfo((prev) => ({ ...prev, [targetLang]: { isDefault } }));
      setLoadToken((token) => token + 1);
    } catch (err) {
      showMessage('error', err?.message || 'Impossible de charger le document.');
    } finally {
      setLoading(false);
    }
  }, [isPayslip, isGenerate, initialHtml, requestId, templateType]);

  // (Re)initialise every time the editor opens
  useEffect(() => {
    if (!isOpen) return;
    if (isGenerate && !requestId) return;
    const startLang = isPayslip ? 'fr' : normalizeLanguage(initialLanguage);
    docsRef.current = {};
    overridesRef.current = {};
    lastRangeRef.current = null;
    setLang(startLang);
    setTab('edit');
    setPages({});
    setValuesByLang({});
    setDocInfo({});
    setLanguagesInfo(null);
    setCustomFields([]);
    setDirty({});
    setUsedKeys([]);
    setSearch('');
    setNotes('');
    setNewField(null);
    setMessage({ type: '', text: '' });
    loadLanguage(startLang);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, templateType, requestId, mode]);

  const refreshCanvasState = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    setUsedKeys(usedFieldKeys(canvas));
    setFit(measureFit(canvas, page));
  }, [page]);

  // Put the document of the current language on the page (not on every keystroke)
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!isOpen || loading || tab !== 'edit' || !canvas || docsRef.current[lang] === undefined) return;
    canvas.innerHTML = toEditorHtml(docsRef.current[lang], labelOf).html;
    decorateChips(canvas, { labelOf, values: isGenerate ? values : null });
    refreshCanvasState();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, loading, tab, lang, loadToken]);

  // Field names / values shown inside the chips follow the side panel
  useEffect(() => {
    if (tab !== 'edit') return;
    decorateChips(canvasRef.current, { labelOf, values: isGenerate ? values : null });
    refreshCanvasState();
  }, [values, labelOf, isGenerate, tab, refreshCanvasState]);

  const previewValues = () => {
    if (isGenerate) return values;
    // Template preview: sample data, with the field name where no sample exists
    const filled = {};
    usedFieldKeys(docsRef.current[lang] || '').forEach((key) => {
      const custom = customFields.find((field) => field.key === key);
      filled[key] = String(values[key] ?? '').trim() || custom?.defaultVal || `[${labelOf(key)}]`;
    });
    return filled;
  };

  const previewHtml = tab === 'preview' ? fillHtml(docsRef.current[lang] || '', previewValues()) : '';

  // Preview: shrink the content like the PDF engine does when it overflows the page(s)
  useEffect(() => {
    const el = previewRef.current;
    if (tab !== 'preview' || !el) return;
    el.style.zoom = '';
    const available = mmToPx(page.heightMm - page.marginTopMm - page.marginBottomMm) * page.pages;
    let zoom = 1;
    if (el.scrollHeight > available + 1) {
      let low = 0.5;
      let high = 1;
      for (let i = 0; i < 7; i += 1) {
        const mid = (low + high) / 2;
        el.style.zoom = mid;
        if (el.getBoundingClientRect().height <= available) low = mid; else high = mid;
      }
      zoom = low;
      el.style.zoom = zoom;
    }
    setPreviewZoom(zoom);
  }, [tab, lang, page, previewHtml]);

  if (!isOpen) return null;

  // ── Editing helpers ────────────────────────────────────────────

  const markDirty = () => setDirty((prev) => (prev[lang] ? prev : { ...prev, [lang]: true }));

  const syncFromCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    docsRef.current[lang] = toStoredHtml(canvas, labelOf);
    refreshCanvasState();
    markDirty();
  };

  const rememberSelection = () => {
    const selection = window.getSelection();
    const canvas = canvasRef.current;
    if (canvas && selection.rangeCount > 0 && canvas.contains(selection.anchorNode)) {
      lastRangeRef.current = selection.getRangeAt(0).cloneRange();
    }
  };

  /** Give the caret back to the page, where it was last (or at the end). */
  const restoreSelection = () => {
    const canvas = canvasRef.current;
    if (!canvas) return false;
    const selection = window.getSelection();
    // Checked before focusing: focus() alone drops the caret at the start of the page
    if (document.activeElement === canvas && selection.rangeCount > 0 && canvas.contains(selection.anchorNode)) {
      return true;
    }
    canvas.focus();
    const range = lastRangeRef.current && canvas.contains(lastRangeRef.current.commonAncestorContainer)
      ? lastRangeRef.current
      : (() => {
        const end = document.createRange();
        end.selectNodeContents(canvas);
        end.collapse(false);
        return end;
      })();
    selection.removeAllRanges();
    selection.addRange(range);
    return true;
  };

  const exec = (command, value = null) => {
    if (!restoreSelection()) return;
    document.execCommand(command, false, value);
    rememberSelection();
    syncFromCanvas();
  };

  const applyFontSize = (pt) => {
    if (!pt || !restoreSelection()) return;
    // execCommand only knows sizes 1-7: mark the selection, then set the real size
    document.execCommand('fontSize', false, '7');
    canvasRef.current.querySelectorAll('font[size="7"]').forEach((font) => {
      const span = document.createElement('span');
      span.style.fontSize = `${pt}pt`;
      span.innerHTML = font.innerHTML;
      font.replaceWith(span);
    });
    syncFromCanvas();
  };

  /**
   * Make the caret a plain insertion point: never replace selected text, and
   * never sit inside an existing field (clicking a field selects it as a block).
   */
  const prepareInsertionPoint = () => {
    if (!restoreSelection()) return false;
    const selection = window.getSelection();
    selection.collapseToEnd();
    const node = selection.anchorNode;
    const element = node?.nodeType === Node.ELEMENT_NODE ? node : node?.parentElement;
    const chip = element?.closest?.('.doc-var');
    if (chip && canvasRef.current.contains(chip)) {
      const after = document.createRange();
      after.setStartAfter(chip);
      after.collapse(true);
      selection.removeAllRanges();
      selection.addRange(after);
    }
    return true;
  };

  /**
   * Insert a field at the caret with the Range API. (execCommand('insertHTML')
   * moves a non-editable element out of its paragraph when the caret is at the
   * end of a line.)
   */
  const insertChipAtCaret = (key, label) => {
    const selection = window.getSelection();
    const range = selection.getRangeAt(0);
    const holder = document.createElement('div');
    holder.innerHTML = chipHtml(key, label);
    const chip = holder.firstChild;
    const space = document.createTextNode(String.fromCharCode(160)); // non-breaking space
    range.insertNode(space);
    range.insertNode(chip);
    const after = document.createRange();
    after.setStartAfter(space);
    after.collapse(true);
    selection.removeAllRanges();
    selection.addRange(after);
  };

  const insertField = (key) => {
    if (tab !== 'edit' || !prepareInsertionPoint()) return;
    insertChipAtCaret(key, labelOf(key));
    decorateChips(canvasRef.current, { labelOf, values: isGenerate ? values : null });
    rememberSelection();
    syncFromCanvas();
  };

  const handleCanvasClick = (e) => {
    rememberSelection();
    const chip = e.target.closest?.('.doc-var[data-var]');
    if (chip && isGenerate) {
      const input = document.getElementById(`dte-value-${chip.dataset.var}`);
      if (input) {
        input.scrollIntoView({ block: 'center', behavior: 'smooth' });
        input.focus();
      }
    }
  };

  const setValue = (key, value) => {
    overridesRef.current[key] = value;
    setValuesByLang((prev) => ({ ...prev, [lang]: { ...(prev[lang] || {}), [key]: value } }));
  };

  const addCustomField = () => {
    const label = (newField?.label || '').trim();
    if (!label) return;
    let key = labelToKey(label) || 'champ';
    if (isSystemField(key)) key = `${key}Perso`;
    const takenKeys = new Set(customFields.map((existing) => existing.key));
    const baseKey = key;
    for (let n = 2; takenKeys.has(key); n += 1) key = `${baseKey}${n}`;
    const field = { key, label, defaultVal: (newField.defaultVal || '').trim() };
    setCustomFields((prev) => [...prev, field]);
    setNewField(null);
    markDirty();
    // Insert it straight away, with its label (state is not updated yet)
    if (tab === 'edit' && prepareInsertionPoint()) {
      insertChipAtCaret(key, label);
      rememberSelection();
      docsRef.current[lang] = toStoredHtml(canvasRef.current, (k) => (k === key ? label : labelOf(k)));
      refreshCanvasState();
    }
  };

  const deleteCustomField = (key) => {
    const field = customFields.find((f) => f.key === key);
    if (!window.confirm(`Supprimer le champ « ${field?.label || key} » ? Il sera retiré du document.`)) return;
    canvasRef.current?.querySelectorAll(`.doc-var[data-var="${key}"]`).forEach((chip) => chip.remove());
    setCustomFields((prev) => prev.filter((f) => f.key !== key));
    if (canvasRef.current && tab === 'edit') syncFromCanvas(); else markDirty();
  };

  const updatePage = (patch) => {
    setPages((prev) => ({ ...prev, [lang]: { ...(prev[lang] || DEFAULT_PAGE_SETTINGS), ...patch } }));
    markDirty();
  };

  const changeLanguage = (nextLang) => {
    if (nextLang === lang || loading) return;
    lastRangeRef.current = null;
    setLang(nextLang);
    if (docsRef.current[nextLang] === undefined) loadLanguage(nextLang);
  };

  const hasUnsavedChanges = Object.values(dirty).some(Boolean);

  const handleClose = () => {
    if (!isGenerate && hasUnsavedChanges
      && !window.confirm('Des modifications ne sont pas enregistrées. Fermer quand même ?')) {
      return;
    }
    onClose();
  };

  // ── Actions ────────────────────────────────────────────────────

  const handleSave = async () => {
    setSaving(true);
    showMessage('', '');
    try {
      if (isPayslip) {
        await api.patch('/salary/template/html', { htmlContent: toBraceHtml(docsRef.current[lang] || '') });
      } else {
        const languagesToSave = Object.keys(dirty).filter((l) => dirty[l] && docsRef.current[l] !== undefined);
        if (languagesToSave.length === 0) languagesToSave.push(lang);
        const customPlaceholders = JSON.stringify(
          customFields.map((field) => ({ ...field, tag: `{${field.key}}`, isCustom: true })),
        );
        for (const l of languagesToSave) {
          await api.patch(`/attestations/templates/${templateType}/html?lang=${l}`, {
            htmlContent: docsRef.current[l],
            customPlaceholders,
            pageSettings: pages[l] || DEFAULT_PAGE_SETTINGS,
          });
        }
        setDocInfo((prev) => {
          const next = { ...prev };
          languagesToSave.forEach((l) => { next[l] = { isDefault: false }; });
          return next;
        });
        setLanguagesInfo((prev) => {
          if (!prev) return prev;
          const next = { ...prev };
          languagesToSave.forEach((l) => { next[l] = { ...(next[l] || {}), customized: true }; });
          return next;
        });
      }
      setDirty({});
      showMessage('success', 'Modèle enregistré avec succès.');
      if (onSaveSuccess) onSaveSuccess();
    } catch (err) {
      showMessage('error', err?.message || "Échec de l'enregistrement du modèle.");
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    const prompt = isGenerate
      ? "Restaurer le modèle d'origine et les données de l'employé ?"
      : 'Remplacer le contenu par le modèle standard ? Il faudra enregistrer pour appliquer.';
    if (!window.confirm(prompt)) return;
    overridesRef.current = {};
    loadLanguage(lang, { forceDefault: !isGenerate }).then(() => {
      if (!isGenerate) markDirty();
      showMessage('info', isGenerate ? 'Document restauré.' : 'Modèle standard chargé.');
    });
  };

  const emptyUsedKeys = isGenerate ? usedKeys.filter((key) => !String(values[key] ?? '').trim()) : [];

  const handleGenerate = () => {
    if (!onGenerate) return;
    if (emptyUsedKeys.length > 0) {
      const names = emptyUsedKeys.map(labelOf).join(', ');
      if (!window.confirm(`Ces informations sont vides et apparaîtront en blanc : ${names}.\n\nGénérer quand même ?`)) return;
    }
    onGenerate({
      html: fillHtml(docsRef.current[lang] || '', values),
      variables: values,
      notes,
      language: lang,
    });
  };

  const handleExactPdf = async () => {
    setExporting(true);
    try {
      const token = localStorage.getItem('pfe_token');
      const response = await fetch(`${api.baseUrl}/attestations/templates/${templateType}/preview-pdf?lang=${lang}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token && { Authorization: `Bearer ${token}` }) },
        body: JSON.stringify({ html: fillHtml(docsRef.current[lang] || '', previewValues()), pageSettings: page }),
      });
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.message || `HTTP ${response.status}`);
      }
      window.open(window.URL.createObjectURL(await response.blob()), '_blank');
    } catch (err) {
      showMessage('error', `Aperçu PDF indisponible : ${err.message}`);
    } finally {
      setExporting(false);
    }
  };

  // ── Render ─────────────────────────────────────────────────────

  const groups = FIELD_GROUPS.filter((group) => (isPayslip
    ? ['employee', 'job', 'general', 'payroll'].includes(group.id)
    : !group.payslipOnly));
  const busy = loading || saving || generating;
  const sheetStyle = {
    width: `${page.widthMm}mm`,
    padding: `${page.marginTopMm}mm ${page.marginRightMm}mm ${page.marginBottomMm}mm ${page.marginLeftMm}mm`,
    backgroundSize: `100% ${page.heightMm}mm`,
  };
  const currentFormat = PAGE_FORMATS.find((fmt) => Math.abs(fmt.widthMm - page.widthMm) < 1.5
    && Math.abs(fmt.heightMm - page.heightMm) < 1.5);
  const pagesLabel = `${page.pages} page${page.pages > 1 ? 's' : ''}`;

  return (
    // Clicks must not reach a parent dialog (the template manager closes on overlay click)
    <div className="modal-overlay editor-modal-overlay" onClick={(e) => e.stopPropagation()}>
      <style>{documentPageCss(page)}</style>
      <div className="dte-modal" role="dialog" aria-modal="true">
        {/* Header */}
        <div className="dte-header">
          <div className="dte-title">
            <FileEdit size={20} />
            <div>
              <h3>
                {isGenerate ? 'Générer le document' : 'Modèle de document'} : <span>{templateName || templateType}</span>
              </h3>
              <p>
                {isGenerate
                  ? `${employeeLabel ? `Pour ${employeeLabel} · ` : ''}vérifiez les informations puis générez le PDF`
                  : 'Écrivez comme dans Word et insérez les informations automatiques depuis la liste de droite'}
              </p>
            </div>
          </div>

          <div className="dte-header-controls">
            {!isPayslip && (
              <div className="dte-segmented" role="radiogroup" aria-label="Langue du document">
                {DOC_LANGUAGES.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    role="radio"
                    aria-checked={lang === option.value}
                    className={lang === option.value ? 'is-on' : ''}
                    onClick={() => changeLanguage(option.value)}
                    disabled={busy}
                    title={languagesInfo?.[option.value]?.customized
                      ? `Version ${option.label} personnalisée`
                      : `Version ${option.label} : modèle standard`}
                  >
                    {option.label}
                    <i className={`dte-dot${languagesInfo?.[option.value]?.customized ? ' is-custom' : ''}`} />
                    {dirty[option.value] && <em>•</em>}
                  </button>
                ))}
              </div>
            )}
            <div className="dte-segmented">
              <button type="button" className={tab === 'edit' ? 'is-on' : ''} onClick={() => setTab('edit')}>
                <FileEdit size={13} /> Édition
              </button>
              <button type="button" className={tab === 'preview' ? 'is-on' : ''} onClick={() => setTab('preview')}>
                <Eye size={13} /> Aperçu
              </button>
              {!isGenerate && (
                <button
                  type="button"
                  className={tab === 'html' ? 'is-on' : ''}
                  onClick={() => setTab('html')}
                  title="Mode avancé : code HTML du modèle"
                >
                  <Code size={13} /> Avancé
                </button>
              )}
            </div>
            <button type="button" className="dte-close" onClick={handleClose} title="Fermer"><X size={18} /></button>
          </div>
        </div>

        {message.text && (
          <div className={`dte-message ${message.type}`}>
            {message.type === 'error' ? <AlertCircle size={15} /> : <CheckCircle2 size={15} />}
            <span>{message.text}</span>
            <button type="button" onClick={() => showMessage('', '')}><X size={13} /></button>
          </div>
        )}

        {/* Formatting toolbar */}
        {tab === 'edit' && (
          <div className="dte-toolbar">
            <button type="button" onMouseDown={keepSelection} onClick={() => exec('undo')} title="Annuler (Ctrl+Z)"><Undo2 size={15} /></button>
            <button type="button" onMouseDown={keepSelection} onClick={() => exec('redo')} title="Rétablir (Ctrl+Y)"><Redo2 size={15} /></button>
            <i />
            <select
              value=""
              onChange={(e) => exec('formatBlock', e.target.value)}
              title="Style du paragraphe"
            >
              <option value="" disabled>Style</option>
              <option value="<p>">Texte normal</option>
              <option value="<h1>">Titre 1</option>
              <option value="<h2>">Titre 2</option>
              <option value="<h3>">Titre 3</option>
            </select>
            <select value="" onChange={(e) => applyFontSize(Number(e.target.value))} title="Taille du texte">
              <option value="" disabled>Taille</option>
              {FONT_SIZES.map((size) => <option key={size} value={size}>{size} pt</option>)}
            </select>
            <i />
            <button type="button" onMouseDown={keepSelection} onClick={() => exec('bold')} title="Gras (Ctrl+B)"><Bold size={15} /></button>
            <button type="button" onMouseDown={keepSelection} onClick={() => exec('italic')} title="Italique (Ctrl+I)"><Italic size={15} /></button>
            <button type="button" onMouseDown={keepSelection} onClick={() => exec('underline')} title="Souligné (Ctrl+U)"><Underline size={15} /></button>
            <i />
            <button type="button" onMouseDown={keepSelection} onClick={() => exec('justifyLeft')} title="Aligner à gauche"><AlignLeft size={15} /></button>
            <button type="button" onMouseDown={keepSelection} onClick={() => exec('justifyCenter')} title="Centrer"><AlignCenter size={15} /></button>
            <button type="button" onMouseDown={keepSelection} onClick={() => exec('justifyRight')} title="Aligner à droite"><AlignRight size={15} /></button>
            <button type="button" onMouseDown={keepSelection} onClick={() => exec('justifyFull')} title="Justifier"><AlignJustify size={15} /></button>
            <i />
            <button type="button" onMouseDown={keepSelection} onClick={() => exec('insertUnorderedList')} title="Liste à puces"><List size={15} /></button>
            <button type="button" onMouseDown={keepSelection} onClick={() => exec('insertOrderedList')} title="Liste numérotée"><ListOrdered size={15} /></button>
            <i />
            <button type="button" onMouseDown={keepSelection} onClick={() => exec('removeFormat')} title="Effacer la mise en forme"><Eraser size={15} /></button>

            <span className={`dte-fit${fit.overflow ? ' is-over' : ''}`}>
              {fit.overflow ? <AlertTriangle size={13} /> : <Check size={13} />}
              {fit.overflow
                ? `Dépasse ${pagesLabel} : sera réduit à ~${Math.max(50, Math.round(100 / fit.ratio))} % pour tenir`
                : `Tient sur ${pagesLabel}`}
              <small>{pageFormatLabel(page)}</small>
            </span>
          </div>
        )}

        {/* Workspace */}
        <div className="dte-workspace">
          <div className="dte-stage">
            {loading && (
              <div className="dte-loading">
                <div className="spinner" />
                <span>Chargement du document…</span>
              </div>
            )}

            {!loading && tab === 'edit' && (
              <div
                className={`dte-sheet${isGenerate ? ' is-generate' : ''}`}
                style={{ ...sheetStyle, minHeight: `${page.heightMm * page.pages}mm` }}
                onClick={(e) => { if (e.target === e.currentTarget) restoreSelection(); }}
              >
                <div
                  ref={canvasRef}
                  className="doc-page dte-canvas"
                  contentEditable
                  suppressContentEditableWarning
                  spellCheck={false}
                  onInput={syncFromCanvas}
                  onKeyUp={rememberSelection}
                  onMouseUp={rememberSelection}
                  onBlur={rememberSelection}
                  onClick={handleCanvasClick}
                />
              </div>
            )}

            {!loading && tab === 'preview' && (
              <>
                <div className="dte-preview-note">
                  {previewZoom < 0.995
                    ? `Contenu réduit à ${Math.round(previewZoom * 100)} % pour tenir sur ${pagesLabel}, comme à l'impression.`
                    : `Aperçu à taille réelle : ${pageFormatLabel(page)}, ${pagesLabel}.`}
                  {!isGenerate && " Les valeurs affichées sont des exemples."}
                </div>
                <div
                  className="dte-sheet is-preview"
                  style={{ ...sheetStyle, height: `${page.heightMm * page.pages}mm` }}
                >
                  <div ref={previewRef} className="doc-page" dangerouslySetInnerHTML={{ __html: previewHtml }} />
                </div>
              </>
            )}

            {!loading && tab === 'html' && (
              <textarea
                key={`${lang}-${loadToken}`}
                className="dte-code"
                defaultValue={docsRef.current[lang] || ''}
                onChange={(e) => { docsRef.current[lang] = e.target.value; markDirty(); }}
                spellCheck={false}
              />
            )}
          </div>

          {/* Side panel */}
          <aside className="dte-side">
            {isGenerate && (
              <section className="dte-panel">
                <h4>Informations du document</h4>
                <p className="dte-hint">Corrigez une valeur ici : la page se met à jour immédiatement.</p>
                {usedKeys.length === 0 && <p className="dte-empty">Ce document ne contient aucun champ automatique.</p>}
                {usedKeys.map((key) => {
                  const empty = !String(values[key] ?? '').trim();
                  return (
                    <label className={`dte-value${empty ? ' is-empty' : ''}`} key={key} htmlFor={`dte-value-${key}`}>
                      <span>{labelOf(key)}{empty && <em>à compléter</em>}</span>
                      <input
                        id={`dte-value-${key}`}
                        type="text"
                        value={values[key] ?? ''}
                        onChange={(e) => setValue(key, e.target.value)}
                      />
                    </label>
                  );
                })}
                <label className="dte-value">
                  <span>Notes RH (internes)</span>
                  <textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Optionnel" />
                </label>
              </section>
            )}

            <section className="dte-panel">
              <h4>{isGenerate ? 'Ajouter une information' : 'Informations automatiques'}</h4>
              <p className="dte-hint">
                {tab === 'edit'
                  ? 'Placez le curseur dans le document puis cliquez sur une information pour l\'insérer.'
                  : 'Revenez dans « Édition » pour insérer une information.'}
              </p>
              <div className="dte-search">
                <Search size={13} />
                <input
                  type="text"
                  placeholder="Rechercher…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
              <FieldCatalog
                groups={groups}
                customFields={customFields}
                lang={lang}
                search={search}
                usedKeys={usedKeys}
                onInsert={insertField}
                onDeleteCustom={isGenerate ? null : deleteCustomField}
                disabled={tab !== 'edit' || loading}
              />

              {!isGenerate && !isPayslip && (
                newField ? (
                  <div className="dte-new-field">
                    <label>
                      <span>Nom de l'information</span>
                      <input
                        type="text"
                        autoFocus
                        placeholder="ex. Nom du tuteur"
                        value={newField.label}
                        onChange={(e) => setNewField({ ...newField, label: e.target.value })}
                        onKeyDown={(e) => { if (e.key === 'Enter') addCustomField(); }}
                      />
                    </label>
                    <label>
                      <span>Valeur par défaut (optionnel)</span>
                      <input
                        type="text"
                        placeholder="ex. M. Ahmed Khalil"
                        value={newField.defaultVal}
                        onChange={(e) => setNewField({ ...newField, defaultVal: e.target.value })}
                        onKeyDown={(e) => { if (e.key === 'Enter') addCustomField(); }}
                      />
                    </label>
                    <div className="dte-new-field-actions">
                      <button type="button" className="dte-btn" onClick={() => setNewField(null)}>Annuler</button>
                      <button type="button" className="dte-btn is-primary" onClick={addCustomField} disabled={!newField.label.trim()}>
                        Ajouter
                      </button>
                    </div>
                  </div>
                ) : (
                  <button type="button" className="dte-btn is-dashed" onClick={() => setNewField({ label: '', defaultVal: '' })}>
                    <Plus size={13} /> Créer une information personnalisée
                  </button>
                )
              )}
            </section>

            {!isGenerate && !isPayslip && (
              <details className="dte-panel dte-page-setup">
                <summary>Mise en page · {pageFormatLabel(page)}</summary>
                <p className="dte-hint">
                  Reprise du fichier Word importé. Le document imprimé garde ce format et ce nombre de pages.
                </p>
                <label>
                  <span>Format</span>
                  <select
                    value={currentFormat?.id || 'custom'}
                    onChange={(e) => {
                      const format = PAGE_FORMATS.find((fmt) => fmt.id === e.target.value);
                      if (format) updatePage({ widthMm: format.widthMm, heightMm: format.heightMm });
                    }}
                  >
                    {PAGE_FORMATS.map((fmt) => <option key={fmt.id} value={fmt.id}>{fmt.label}</option>)}
                    {!currentFormat && <option value="custom">Personnalisé ({pageFormatLabel(page)})</option>}
                  </select>
                </label>
                <label>
                  <span>Nombre de pages à respecter</span>
                  <input
                    type="number"
                    min={1}
                    max={20}
                    value={page.pages}
                    onChange={(e) => updatePage({ pages: Math.min(20, Math.max(1, Number(e.target.value) || 1)) })}
                  />
                </label>
                <div className="dte-margins">
                  <span>Marges (mm)</span>
                  {[['marginTopMm', 'Haut'], ['marginBottomMm', 'Bas'], ['marginLeftMm', 'Gauche'], ['marginRightMm', 'Droite']].map(([key, text]) => (
                    <label key={key}>
                      <small>{text}</small>
                      <input
                        type="number"
                        min={0}
                        max={100}
                        step={0.5}
                        value={page[key]}
                        onChange={(e) => updatePage({ [key]: Math.min(100, Math.max(0, Number(e.target.value) || 0)) })}
                      />
                    </label>
                  ))}
                </div>
              </details>
            )}
          </aside>
        </div>

        {/* Footer */}
        <div className="dte-footer">
          <button type="button" className="dte-btn" onClick={handleReset} disabled={busy}>
            <RotateCcw size={14} /> {isGenerate ? 'Restaurer' : 'Modèle standard'}
          </button>
          {docInfo[lang]?.isDefault && !dirty[lang] && !isGenerate && (
            <span className="dte-footer-note">Version standard : modifiez-la puis enregistrez pour la personnaliser.</span>
          )}
          {isGenerate && docInfo[lang]?.isDefault && (
            <span className="dte-footer-note">Aucun modèle personnalisé pour cette langue : modèle standard utilisé.</span>
          )}
          <div className="dte-footer-right">
            <button type="button" className="dte-btn" onClick={handleClose} disabled={saving || generating}>
              {isGenerate ? 'Annuler' : 'Fermer'}
            </button>
            {!isPayslip && (
              <button type="button" className="dte-btn" onClick={handleExactPdf} disabled={busy || exporting}>
                <FileDown size={14} /> {exporting ? 'Préparation…' : 'Tester le PDF'}
              </button>
            )}
            {isGenerate ? (
              <button type="button" className="dte-btn is-success" onClick={handleGenerate} disabled={busy}>
                <Save size={14} /> {generating ? 'Génération en cours…' : 'Approuver & générer le PDF'}
              </button>
            ) : (
              <button type="button" className="dte-btn is-success" onClick={handleSave} disabled={busy}>
                <Save size={14} /> {saving ? 'Enregistrement…' : 'Enregistrer le modèle'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default InAppTemplateEditorModal;
